'use client';

import { useEffect } from 'react';
import { apiBase, fetchWithTimeout } from '@/lib/api';

type Props = {
  postId: string;
};

function getCookie(name: string): string | null {
  try {
    const all = typeof document !== 'undefined' ? document.cookie : '';
    if (!all) return null;
    const parts = all.split(';').map((p) => p.trim());
    for (const p of parts) {
      if (!p.startsWith(`${name}=`)) continue;
      return decodeURIComponent(p.slice(name.length + 1));
    }
    return null;
  } catch (e) {
    console.error('[cookie取得エラー]', e);
    return null;
  }
}

function setCookie(name: string, value: string, days: number) {
  try {
    const maxAge = days * 24 * 60 * 60;
    // NOTE: Next.js側のフロントで発行するため、SameSite/Lax で十分（外部送信しない）
    document.cookie = `${name}=${encodeURIComponent(
      value
    )}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
  } catch (e) {
    console.error('[cookie設定エラー]', e);
  }
}

function ensureVisitorId(): string {
  const key = 'yuikai_vid';
  const existing = getCookie(key);
  if (existing) return existing;

  // crypto.randomUUID が使える環境ではUUIDを採用
  const next =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? (crypto as any).randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  // 目的: 長期的に同一訪問者を識別できるようにする（PIIは含めない）
  setCookie(key, next, 365);
  return next;
}

/**
 * ブログ詳細ページの閲覧計測（日次ユニーク）
 *
 * 目的:
 * - 「同一訪問者 × 同一記事 × 同一日(JST)」は1回だけカウントする
 * - 管理画面でPVとして表示する（実体はUV）
 */
export function PostViewTracker({ postId }: Props) {
  useEffect(() => {
    if (!postId) return;

    let cancelled = false;
    (async () => {
      try {
        const visitorId = ensureVisitorId();
        const url = `${apiBase()}/api/posts/${postId}/view`;
        const res = await fetchWithTimeout(
          url,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ visitorId }),
          },
          8000
        );
        if (!res.ok) return;
        // 返却値（counted）は現状UIに使わないが、将来のデバッグに備えて読む
        if (!cancelled) {
          await res.json().catch(() => null);
        }
      } catch (e) {
        // UIは落とさない（閲覧計測が失敗しても記事閲覧自体は継続する）
        console.error('[ブログ閲覧計測エラー]', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [postId]);

  return null;
}

